<div class="user-edit">
  <div class="user-edit__content">
    <div class="user-edit__layout">
      <div class="user-edit__header">
        <div class="user-edit__heading">
          <h1 class="page-title">Edit User Profile</h1>
          @if (user) {
          <p class="page-subtitle">Manage details for {{ user.name }}.</p>
          }
        </div>
        <div class="user-edit__actions">
          <app-button type="button" variant="secondary" (click)="goBack()">Back</app-button>
          <app-button type="submit" form="profileForm" [disabled]="isProfileSaveDisabled">
            {{ isSaving ? "Saving..." : "Save changes" }}
          </app-button>
        </div>
      </div>

      @if (isReady) {
      <app-card>
        <form id="profileForm" [formGroup]="profileForm" (ngSubmit)="saveUser()" class="user-edit__form">
          <div class="user-edit__form-field user-edit__form-field--full-width">
            <div class="user-edit__avatar-section">
              <app-avatar
                class="user-edit__avatar-preview"
                [imageUrl]="user?.imageUrl ?? null"
                [name]="user?.name ?? ''"
                size="2xl"></app-avatar>
              <div class="user-edit__avatar-details">
                <p class="user-edit__avatar-name">{{ user?.name || "Unknown user" }}</p>
                <p class="user-edit__avatar-meta">{{ user?.email || "No email available" }}</p>
                @if (user?.role?.name) {
                <p class="user-edit__avatar-meta">{{ user?.role?.name }}</p>
                }
              </div>
              <div class="user-edit__avatar-actions">
                <input type="file" #avatarInput accept="image/*" (change)="onAvatarChange($event)" style="display: none" />
                <app-button type="button" variant="secondary" (click)="avatarInput.click()"> Change Image </app-button>
                @if (user?.imageUrl) {
                <app-button type="button" variant="secondary" (click)="removeAvatar()"> Remove Image </app-button>
                }
              </div>
            </div>
          </div>

          <div class="user-edit__form-grid">
            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="person"></ng-glyph>
                <label for="name" class="user-edit__form-label">Name</label>
              </div>
              <input
                id="name"
                type="text"
                formControlName="name"
                class="user-edit__form-input"
                [class.user-edit__form-input--error]="profileForm.get('name')?.invalid && profileForm.get('name')?.touched" />
              @if (profileForm.get('name')?.invalid && profileForm.get('name')?.touched) {
              <div class="user-edit__form-error">Name is required and must be at least 2 characters</div>
              }
            </div>

            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="mail"></ng-glyph>
                <label for="email" class="user-edit__form-label">Email</label>
              </div>
              <input
                id="email"
                type="email"
                formControlName="email"
                class="user-edit__form-input"
                [class.user-edit__form-input--error]="profileForm.get('email')?.invalid && profileForm.get('email')?.touched" />
              @if (profileForm.get('email')?.invalid && profileForm.get('email')?.touched) {
              <div class="user-edit__form-error">A valid email is required</div>
              }
            </div>

            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="call"></ng-glyph>
                <label for="phone" class="user-edit__form-label">Phone</label>
              </div>
              <input id="phone" type="tel" formControlName="phone" class="user-edit__form-input" />
            </div>

            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="group"></ng-glyph>
                <label for="role" class="user-edit__form-label">Role</label>
              </div>
              <select
                id="role"
                formControlName="role"
                class="user-edit__form-input"
                [class.user-edit__form-input--error]="profileForm.get('role')?.invalid && profileForm.get('role')?.touched">
                <option value="">Select role</option>
                @for (role of roles; track role.id) {
                <option [value]="role.id">{{ role.name }}</option>
                }
              </select>
              @if (profileForm.get('role')?.invalid && profileForm.get('role')?.touched) {
              <div class="user-edit__form-error">Role is required.</div>
              }
            </div>

            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="location_on"></ng-glyph>
                <label for="address" class="user-edit__form-label">Address</label>
              </div>
              <input id="address" type="text" formControlName="address" class="user-edit__form-input" />
            </div>
          </div>
        </form>
      </app-card>
      } @if (isReady) {
      <app-card class="user-edit__password-card">
        <form [formGroup]="passwordForm" (ngSubmit)="onChangePassword()" class="user-edit__form">
          <div class="user-edit__section-header">
            <div>
              <h2 class="user-edit__section-title">Change password</h2>
              <p class="user-edit__section-hint">Use a new password with at least 8 characters.</p>
            </div>
          </div>

          <div class="user-edit__form-grid">
            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="key"></ng-glyph>
                <label for="newPassword" class="user-edit__form-label">New password</label>
              </div>
              <input
                id="newPassword"
                type="password"
                formControlName="newPassword"
                class="user-edit__form-input"
                [class.user-edit__form-input--error]="passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched" />
              @if (passwordForm.get('newPassword')?.invalid && passwordForm.get('newPassword')?.touched) {
              <div class="user-edit__form-error">New password must be at least 8 characters.</div>
              }
            </div>

            <div class="user-edit__form-field user-edit__form-field--spaced">
              <div class="user-edit__form-label-row">
                <ng-glyph class="user-edit__form-label-icon" name="key"></ng-glyph>
                <label for="confirmPassword" class="user-edit__form-label">Confirm password</label>
              </div>
              <input
                id="confirmPassword"
                type="password"
                formControlName="confirmPassword"
                class="user-edit__form-input"
                [class.user-edit__form-input--error]="passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched" />
              @if (passwordForm.hasError('passwordMismatch') && passwordForm.get('confirmPassword')?.touched) {
              <div class="user-edit__form-error">Passwords do not match.</div>
              }
            </div>
          </div>

          <div class="user-edit__form-actions">
            <app-button type="submit" [disabled]="isPasswordSubmitDisabled">
              {{ isPasswordSaving ? "Updating..." : "Update password" }}
            </app-button>
          </div>
        </form>
      </app-card>
      }
    </div>
  </div>
</div>
